import { useRouter } from "@tanstack/react-router"
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react"
import { scrollToDocAnchor, useDocLinks } from "./doc-links"

/**
 * Links in agent output (decision 0029, proposal §6): every agent-supplied URL, in markdown or in a tool body, goes
 * through {@link linkTarget} and {@link SafeLink}. `javascript:`, `data:` and other non-http(s) schemes render as text;
 * links to our own origin (or relative ones) open in place through the router, with no confirmation; external links
 * open in a new tab after a confirmation that shows the URL. In the main chunk (no Streamdown), so tool bodies can use
 * it without loading the markdown chunk.
 */

/** Where a link goes: in the app (same origin or relative), outside (http/https/mailto), or nowhere (unsafe). */
export const linkTarget = (
  href: string | undefined,
  origin: string = globalThis.location?.origin ?? "",
): { readonly kind: "internal" | "external"; readonly href: string } | null => {
  if (!href) return null
  const h = href.trim()
  if (h.startsWith("#")) return { kind: "internal", href: h }
  if (h.startsWith("/")) {
    // `//host` and `/\host` are protocol-relative to a browser: only a path that stays on our origin is internal.
    const base = "https://minga.invalid"
    let rel: URL
    try {
      rel = new URL(h, base)
    } catch {
      return null
    }
    return rel.origin === base && !/^\/[/\\]/.test(h)
      ? { kind: "internal", href: rel.pathname + rel.search + rel.hash }
      : null
  }
  let url: URL
  try {
    url = new URL(h)
  } catch {
    return null
  }
  if (origin && url.origin === origin)
    return /^\/[/\\]/.test(url.pathname)
      ? null
      : { kind: "internal", href: url.pathname + url.search + url.hash }
  if (url.protocol === "http:" || url.protocol === "https:" || url.protocol === "mailto:")
    return { kind: "external", href: url.href }
  return null
}

/** A plain left click with no modifier: the one we handle in place (others keep the browser's own behaviour). */
const plainClick = (event: React.MouseEvent) =>
  !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)

/** A link inside the app: a real anchor, routed in place on a plain click (no full reload, the chat socket stays). */
function InternalLink({
  href,
  children,
  className,
}: {
  href: string
  children: ReactNode
  className?: string
}) {
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined
  return (
    <a
      href={href}
      className={className}
      onClick={(event) => {
        if (!router || href.startsWith("#") || !plainClick(event)) return
        event.preventDefault()
        router.history.push(href)
      }}
    >
      {children}
    </a>
  )
}

/** An external link: a real anchor (long-press, middle-click work), confirmed with its full URL on a plain click. */
export function ExternalLink({
  href,
  children,
  className,
}: {
  href: string
  children: ReactNode
  className?: string | undefined
}) {
  const [open, setOpen] = useState(false)
  const dialog = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const d = dialog.current
    if (!d) return
    if (open && !d.open) d.showModal()
    if (!open && d.open) d.close()
  }, [open])
  return (
    <>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={href}
        className={className}
        onClick={(event) => {
          if (!plainClick(event)) return
          event.preventDefault()
          setOpen(true)
        }}
      >
        {children}
      </a>
      {open && (
        <dialog
          ref={dialog}
          onClose={() => setOpen(false)}
          className="m-auto w-[min(420px,calc(100vw-32px))] rounded-card border border-line bg-surface p-4 text-ink shadow-sheet"
        >
          <p className="font-semibold">Open an external link?</p>
          <p className="mt-1 font-mono text-[12px] break-all text-ink-2">{href}</p>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              className="h-8 rounded-control px-3 text-sm hover:bg-chip"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-8 items-center rounded-control bg-ink px-3 text-sm text-white no-underline hover:text-white"
              onClick={() => setOpen(false)}
            >
              Open link
            </a>
          </div>
        </dialog>
      )}
    </>
  )
}

/** A project page's own path (`/o/O-19`, `/i/REP-7`, `/k/K-1`, `/g/G-2`, `/m/M-3`, `/agents/lea`, `/search`, `/settings`). */
const PROJECT_PATH = /^\/(?:[oikgm]\/[^/?#]+|agents\/[^/?#]+|search|settings)(?:[/?#]|$)/

/**
 * Agents copy project-relative paths from Minga's tools (`/o/O-19`). Inside a project they resolve under that project
 * (`/w/<ws>/p/<project>/o/O-19`); elsewhere, or for any other href, unchanged.
 */
export const projectHref = (
  href: string | undefined,
  at: { readonly workspaceId?: string | undefined; readonly projectId?: string | undefined },
): string | undefined => {
  const h = href?.trim()
  if (!h || !at.workspaceId || !at.projectId || !PROJECT_PATH.test(h)) return href
  return `/w/${encodeURIComponent(at.workspaceId)}/p/${encodeURIComponent(at.projectId)}${h}`
}

/** The workspace and project of an app path (`/w/<ws>/p/<project>/…`); markdown also renders outside the router. */
export const projectOfPath = (
  path: string | undefined,
): { readonly workspaceId?: string; readonly projectId?: string } => {
  const m = /^\/w\/([^/]+)\/p\/([^/?#]+)/.exec(path ?? "")
  return m
    ? { workspaceId: decodeURIComponent(m[1] as string), projectId: decodeURIComponent(m[2] as string) }
    : {}
}

/**
 * The project that project-relative links (`/o/O-19`) resolve under when the page itself is not inside it, e.g. a
 * lead's brief on the workspace Home (UX pass 3, C1). Without a provider, the current location's project.
 */
export const LinkProjectContext = createContext<{
  readonly workspaceId?: string | undefined
  readonly projectId?: string | undefined
} | null>(null)

/**
 * Any agent-supplied URL as a link: checked, routed in place when internal, confirmed when external, text if unsafe.
 * Inside a project document (wave DOC, `DocLinks`), a relative link resolves among the project's documents first: a
 * live document opens its page; anything else relative is text titled "not imported".
 */
export function SafeLink({
  href,
  children,
  className,
}: {
  href: string | undefined
  children: ReactNode
  className?: string | undefined
}) {
  const docs = useDocLinks()
  const project = useContext(LinkProjectContext)
  href = projectHref(href, project ?? projectOfPath(globalThis.location?.pathname))
  // an in-page `#anchor` of a document: its headings' ids are `doc-<slug>`, so the browser's own jump can't find them
  if (docs && href?.trim().startsWith("#")) {
    const hash = href.trim()
    return (
      <a
        href={hash}
        className={className}
        onClick={(event) => {
          if (!plainClick(event)) return
          event.preventDefault()
          if (scrollToDocAnchor(hash)) globalThis.history?.replaceState(globalThis.history.state, "", hash)
        }}
      >
        {children}
      </a>
    )
  }
  const doc = docs && href ? docs.resolve(href) : null
  if (doc?.kind === "page")
    return (
      <InternalLink href={doc.href} {...(className ? { className } : {})}>
        {children}
      </InternalLink>
    )
  if (doc?.kind === "missing")
    return (
      <span
        className={`${className ?? ""} underline decoration-dotted decoration-line-control underline-offset-2 text-ink-2`.trim()}
        title={`not imported: ${doc.path}`}
        data-doc-missing=""
      >
        {children}
        <span className="sr-only"> (not in Minga)</span>
      </span>
    )
  const target = linkTarget(href)
  if (!target) return <span className={className}>{children}</span>
  if (target.kind === "internal")
    return (
      <InternalLink href={target.href} {...(className ? { className } : {})}>
        {children}
      </InternalLink>
    )
  return (
    <ExternalLink href={target.href} className={className}>
      {children}
    </ExternalLink>
  )
}
